/**
 * Form state for a cash flow, and the mapping between it and the API.
 *
 * Kept apart from the component so the file exports either UI or logic, never both —
 * mixing them breaks React fast refresh, and this half is the part worth testing anyway.
 */
import type { CashFlow, CashFlowInput } from '../api/generated/model'
import {
  blankEstimation,
  estimationDraftFrom,
  toEstimation,
  type EstimationDraft,
} from '../lib/estimation'
import { toMajor, toMinor } from '../lib/money'

const FREQUENCIES = ['WEEKLY', 'BIWEEKLY', 'MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL'] as const
const CONVENTIONS = ['UNADJUSTED', 'FOLLOWING', 'PRECEDING', 'MODIFIED_FOLLOWING'] as const

/** Empty form state for a new cash flow, defaulted to the most common answers. */
export function blankDraft(accountId: string, currency: string, calendarId: string): Draft {
  return {
    id: '',
    accountId,
    name: '',
    direction: 'OUT',
    amount: '',
    currency,
    calendarId,
    convention: 'FOLLOWING',
    kind: 'RECURRING',
    anchor: new Date().toISOString().slice(0, 10),
    frequency: 'MONTHLY',
    interval: '1',
    monthEndRule: 'CLAMP',
    endKind: 'NEVER',
    endDate: '',
    endCount: '',
    dates: '',
    estimation: blankEstimation(),
  }
}

export type Draft = {
  id: string
  accountId: string
  name: string
  direction: 'IN' | 'OUT'
  amount: string
  currency: string
  calendarId: string
  convention: (typeof CONVENTIONS)[number]
  kind: 'RECURRING' | 'EXPLICIT'
  anchor: string
  frequency: (typeof FREQUENCIES)[number]
  interval: string
  monthEndRule: 'CLAMP' | 'LAST_DAY_OF_MONTH'
  endKind: 'NEVER' | 'ON_DATE' | 'AFTER_OCCURRENCES'
  endDate: string
  endCount: string
  dates: string
  /** How the amount is worked out. FIXED means the amount above is the amount. */
  estimation: EstimationDraft
}

export function draftFrom(flow: CashFlow): Draft {
  return {
    id: flow.id,
    accountId: flow.accountId,
    name: flow.name,
    direction: flow.direction,
    amount: String(toMajor(flow.amountMinor, flow.currency)),
    currency: flow.currency,
    calendarId: flow.calendarId,
    convention: flow.convention,
    kind: flow.schedule.kind,
    anchor: flow.schedule.anchor ?? '',
    frequency: flow.schedule.frequency ?? 'MONTHLY',
    interval: String(flow.schedule.interval ?? 1),
    monthEndRule: flow.schedule.monthEndRule ?? 'CLAMP',
    endKind: flow.schedule.endKind ?? 'NEVER',
    endDate: flow.schedule.endDate ?? '',
    endCount: flow.schedule.endCount ? String(flow.schedule.endCount) : '',
    dates: (flow.schedule.dates ?? []).join('\n'),
    estimation: estimationDraftFrom(flow.estimation),
  }
}

/**
 * Turns form text into the API's shape.
 *
 * Returns an error string rather than throwing, because the things that can go wrong here
 * are the user's ordinary mistakes — a blank amount, no dates chosen — and they belong
 * next to the field, not in a stack trace. Anything subtler is left to the server, which
 * owns the real rules.
 */
export function toInput(draft: Draft): { input: CashFlowInput } | { error: string } {
  if (!draft.name.trim()) return { error: 'Give it a name.' }
  if (!draft.id.trim()) return { error: 'An id is required.' }

  const amountMinor = toMinor(draft.amount, draft.currency)
  if (amountMinor === null) return { error: 'Amount must be a number.' }
  if (amountMinor < 0) return { error: 'Amount must not be negative — use the direction instead.' }

  const dates = draft.dates
    .split(/[\n,]/)
    .map((d) => d.trim())
    .filter(Boolean)
  if (draft.kind === 'EXPLICIT' && dates.length === 0) {
    return { error: 'Add at least one date, one per line.' }
  }
  if (draft.kind === 'RECURRING' && !draft.anchor) {
    return { error: 'A repeating cash flow needs a first date.' }
  }

  return {
    input: {
      id: draft.id.trim(),
      accountId: draft.accountId,
      name: draft.name.trim(),
      direction: draft.direction,
      amountMinor,
      currency: draft.currency,
      convention: draft.convention,
      calendarId: draft.calendarId,
      // Undefined for a stated amount; the amount above then becomes the fallback the
      // server uses until it has enough history to derive one.
      estimation: toEstimation(draft.estimation),
      schedule:
        draft.kind === 'EXPLICIT'
          ? { kind: 'EXPLICIT', dates }
          : {
              kind: 'RECURRING',
              anchor: draft.anchor,
              frequency: draft.frequency,
              interval: Number(draft.interval) || 1,
              monthEndRule: draft.monthEndRule,
              endKind: draft.endKind,
              endDate: draft.endKind === 'ON_DATE' ? draft.endDate : undefined,
              endCount:
                draft.endKind === 'AFTER_OCCURRENCES' ? Number(draft.endCount) || 1 : undefined,
            },
    },
  }
}

/** Suggests a stable id from the name, so the user rarely has to think about it. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
}


export const FREQUENCY_OPTIONS = FREQUENCIES
export const CONVENTION_OPTIONS = CONVENTIONS
